import { TFunction } from 'i18next';
import { AIActionBinding, createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { processStartTemplate } from 'models/copilot/actions/aiActionTemplates';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotPanelContentBase, CopilotPanelContext } from 'models/copilot/copilotBridge';
import {
  CopilotPanelContextDefinition,
  CopilotPanelEntityRef,
  CopilotPanelProcessCompletionRef,
  CopilotPanelProcessState,
  CopilotPanelProgressRef,
  CopilotPanelRuntime,
} from 'models/copilot/copilotPanelContextDefinition';
import { CopilotTaskEvidenceAuditContent } from 'models/copilot/Processes/TaskEvidenceAudit/copilotTaskEvidenceAudit';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { EntityTypes } from 'models/entity';
import { apiCopilotExecuteProcessTaskEvidenceAudit } from 'services/Api/copilotService';
import { ProgressHubProcess } from 'services/SignalR/ProgressHub';

export class CopilotTaskEvidenceAuditPanelContextDefinition extends CopilotPanelContextDefinition {
  constructor() {
    super();
    this.context = CopilotPanelContext.TaskEvidenceAudit;
    this.name = 'TaskEvidenceAudit';
    this.supportsChat = true;
    this.requiresActiveAction = true;
  }

  getBindings(_content: CopilotPanelContentBase | undefined): AIActionBinding[] {
    return [createAIActionBinding(processStartTemplate, { processId: TenantProcess.TaskEvidenceAudit })];
  }

  getProgressRef(
    runtime: Pick<CopilotPanelRuntime, 'action' | 'actions' | 'content'>,
  ): CopilotPanelProgressRef | undefined {
    const action = this.getSelectedRunningTaskAuditAction(runtime);
    if (!action?.processId) return undefined;

    const entityRef = this.getEntityRef(runtime.content);
    if (!entityRef) return undefined;

    return {
      ...entityRef,
      tenantProcess: action.processId,
      hubProcess: ProgressHubProcess.TaskEvidenceAudit,
    };
  }

  getProcessCompletionRefAfterEntitySelection(
    runtime: Pick<CopilotPanelRuntime, 'action' | 'actions' | 'content'>,
  ): CopilotPanelProcessCompletionRef | undefined {
    const action = this.getSelectedRunningTaskAuditAction(runtime);
    if (!action?.processId) return undefined;

    const entityRef = this.getEntityRef(runtime.content);
    if (!entityRef) return undefined;

    return { ...entityRef, processId: action.processId };
  }

  private getSelectedRunningTaskAuditAction(
    runtime: Pick<CopilotPanelRuntime, 'action'>,
  ): CopilotAction | undefined {
    if (runtime.action?.status === TenantProcessStatus.InProgress && this.isTaskAuditAction(runtime.action)) {
      return runtime.action;
    }

    return undefined;
  }

  async getAnswer(runtime: CopilotPanelRuntime, _t: TFunction<string[]>): Promise<void> {
    const { action } = runtime;
    if (!action || !this.isTaskAuditAction(action)) {
      return;
    }

    const content = runtime.content as CopilotTaskEvidenceAuditContent | undefined;
    if (!content?.taskId) return;

    runtime.setIsLoading(true);
    const answer = await apiCopilotExecuteProcessTaskEvidenceAudit(
      action.status || TenantProcessStatus.NotStarted,
      runtime.conversation,
      content.taskId,
      runtime.connectionId,
      runtime.accessToken,
    );
    if (!answer) return;

    runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, answer.conversation));
  }

  getEntityRef(content: CopilotPanelContentBase | undefined): CopilotPanelEntityRef | undefined {
    if (!this.isAvailableForContent(content)) return undefined;
    const auditContent = content as CopilotTaskEvidenceAuditContent;

    return { entityType: EntityTypes.Task, entityId: auditContent.taskId };
  }

  canStartChat(_actions: CopilotAction[]): boolean {
    return false;
  }

  getWelcomeMessages(action: CopilotAction, t: TFunction<string[]>): string[] {
    if (!this.isTaskAuditAction(action)) {
      return super.getWelcomeMessages(action, t);
    }

    return [];
  }

  isPersistedProcessStateReady(state: CopilotPanelProcessState): boolean {
    if (!this.isTaskAuditProcess(state.processId)) {
      return super.isPersistedProcessStateReady(state);
    }

    if (state.status === TenantProcessStatus.Completed || state.status === TenantProcessStatus.Failed) {
      return true;
    }

    return this.hasConversationContent(state.conversation);
  }

  private isTaskAuditAction(action: CopilotAction): boolean {
    return this.isTaskAuditProcess(action.processId);
  }

  private isTaskAuditProcess(processId: TenantProcess | undefined): boolean {
    return processId === TenantProcess.TaskEvidenceAudit;
  }
}
